import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, ArrowRight, Loader2, Check, Sparkles, Linkedin, Mail, User, Briefcase, MessageSquare } from 'lucide-react';
import axios from 'axios';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const SPRING = { type: 'spring', stiffness: 700, damping: 22, mass: 0.6 };
const REVEAL = { type: 'spring', stiffness: 520, damping: 26, mass: 0.6 };

const inputClasses =
  'w-full pl-12 pr-4 py-4 bg-black/40 border border-white/10 focus:border-[#EF5B5B]/60 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#EF5B5B]/40 smooth';

const Field = ({ id, label, icon: Icon, error, children }) => (
  <div>
    <label htmlFor={id} className="block text-xs uppercase tracking-[0.2em] text-gray-400 mb-2 font-semibold">
      {label}
    </label>
    <div className="relative">
      {Icon && <Icon className="absolute left-4 top-4 w-5 h-5 text-gray-500" />}
      {children}
    </div>
    {error && <p className="mt-2 text-xs text-[#EF5B5B]">{error}</p>}
  </div>
);

const ApplyPage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '',
    email: '',
    linkedin_url: '',
    expertise: '',
    pitch: '',
  });
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(3);

  // Once submitted, count down and redirect to /episodes
  useEffect(() => {
    if (status !== 'success') return undefined;
    setCountdown(3);
    const tick = setInterval(() => {
      setCountdown((c) => (c > 0 ? c - 1 : 0));
    }, 1000);
    const timeout = setTimeout(() => navigate('/episodes'), 3000);
    return () => { clearInterval(tick); clearTimeout(timeout); };
  }, [status, navigate]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.pitch) {
      setError('Name, email, and your pitch are required.');
      return;
    }
    setError('');
    setStatus('loading');
    try {
      await axios.post(`${API}/speaker-applications`, form);
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    }
  };

  return (
    <div className="pt-28 pb-20 px-4 sm:px-6 relative" data-testid="apply-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.header
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={REVEAL}
          className="mb-12 text-center"
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ ...REVEAL, delay: 0.05 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#EF5B5B]/10 border border-[#EF5B5B]/30 text-xs sm:text-sm font-semibold text-[#EF5B5B] tracking-[0.2em] uppercase mb-5"
          >
            <Sparkles className="w-4 h-4" /> Now Accepting Guest Applications
          </motion.span>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight">
            Apply to <span className="gradient-text">Speak</span>
          </h1>
          <p className="text-base sm:text-lg text-gray-400 mt-4 max-w-2xl mx-auto leading-relaxed">
            AI Revolution Digest features CEOs, founders, professors, and operators reshaping how business
            uses AI. If that's you — pitch me a conversation worth an hour of a global executive audience.
          </p>
        </motion.header>

        {/* Card */}
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ ...REVEAL, delay: 0.08 }}
          className="relative rounded-3xl overflow-hidden border border-[#EF5B5B]/25"
          style={{
            background:
              'radial-gradient(120% 90% at 0% 0%, rgba(239,91,91,0.14), transparent 55%),' +
              'radial-gradient(120% 90% at 100% 100%, rgba(239,91,91,0.10), transparent 55%),' +
              'linear-gradient(180deg, rgba(15,10,12,0.9), rgba(10,10,10,0.95))',
            boxShadow: '0 0 0 1px rgba(239,91,91,0.15), 0 30px 80px -30px rgba(239,91,91,0.45)',
          }}
        >
          <AnimatePresence mode="wait">
            {status === 'success' ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={SPRING}
                className="p-10 sm:p-16 text-center"
                data-testid="apply-success"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 18, delay: 0.1 }}
                  className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#EF5B5B]/15 border border-[#EF5B5B]/40 mb-6 shadow-[0_0_40px_rgba(239,91,91,0.55)]"
                >
                  <Check className="w-10 h-10 text-[#EF5B5B]" strokeWidth={3} />
                </motion.div>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
                  Thanks for reaching out!
                </h2>
                <p className="text-gray-300 max-w-md mx-auto leading-relaxed text-base sm:text-lg">
                  We've received your application and will be in touch soon.
                </p>
                <p className="text-sm text-gray-500 mt-6" data-testid="apply-redirect-notice">
                  Taking you back to the show{countdown > 0 ? ` in ${countdown}…` : '…'}
                </p>
                <motion.div
                  className="mt-8 mx-auto h-1 w-40 rounded-full bg-white/10 overflow-hidden"
                  aria-hidden
                >
                  <motion.div
                    initial={{ width: '0%' }}
                    animate={{ width: '100%' }}
                    transition={{ duration: 3, ease: 'linear' }}
                    className="h-full bg-[#EF5B5B]"
                    style={{ boxShadow: '0 0 12px rgba(239,91,91,0.7)' }}
                  />
                </motion.div>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onSubmit={submit}
                className="p-8 sm:p-12 space-y-6"
                data-testid="apply-form"
              >
                <div className="grid sm:grid-cols-2 gap-6">
                  <Field id="name" label="Your Name" icon={User}>
                    <input
                      id="name"
                      type="text"
                      required
                      placeholder="Jane Doe"
                      value={form.name}
                      onChange={update('name')}
                      className={inputClasses}
                      data-testid="apply-name"
                    />
                  </Field>
                  <Field id="email" label="Email" icon={Mail}>
                    <input
                      id="email"
                      type="email"
                      required
                      placeholder="jane@company.com"
                      value={form.email}
                      onChange={update('email')}
                      className={inputClasses}
                      data-testid="apply-email"
                    />
                  </Field>
                </div>

                <Field id="linkedin" label="LinkedIn URL" icon={Linkedin}>
                  <input
                    id="linkedin"
                    type="url"
                    placeholder="https://linkedin.com/in/janedoe"
                    value={form.linkedin_url}
                    onChange={update('linkedin_url')}
                    className={inputClasses}
                    data-testid="apply-linkedin"
                  />
                </Field>

                <Field id="expertise" label="Expertise / Title" icon={Briefcase}>
                  <input
                    id="expertise"
                    type="text"
                    placeholder="CEO of X · Professor of Y · AI Researcher"
                    value={form.expertise}
                    onChange={update('expertise')}
                    className={inputClasses}
                    data-testid="apply-expertise"
                  />
                </Field>

                <Field id="pitch" label="Your Pitch" icon={MessageSquare}>
                  <textarea
                    id="pitch"
                    required
                    rows={6}
                    placeholder="What is the single sharp idea, argument, or story you'd want to explore on the show? What data, experience, or unique lens do you bring? (150–400 words.)"
                    value={form.pitch}
                    onChange={update('pitch')}
                    className={`${inputClasses} pt-4 resize-y min-h-[160px]`}
                    data-testid="apply-pitch"
                  />
                </Field>

                {error && (
                  <p className="text-sm text-[#EF5B5B]" data-testid="apply-error">{error}</p>
                )}

                <div className="flex items-center justify-between gap-4 pt-2">
                  <p className="text-xs text-gray-500 max-w-sm">
                    By submitting you agree to be contacted about your application. Read every pitch personally — no auto-replies.
                  </p>
                  <motion.button
                    type="submit"
                    disabled={status === 'loading'}
                    whileHover={{ scale: 1.04, y: -2 }}
                    whileTap={{ scale: 0.97 }}
                    transition={SPRING}
                    data-testid="apply-submit"
                    className="inline-flex items-center gap-2 px-8 py-4 btn-red rounded-full font-bold text-base whitespace-nowrap disabled:opacity-70"
                  >
                    {status === 'loading' ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" /> Sending…
                      </>
                    ) : (
                      <>
                        <Mic className="w-5 h-5" /> Submit Pitch
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </motion.button>
                </div>
              </motion.form>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Footer helper */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...REVEAL, delay: 0.2 }}
          className="mt-12 grid sm:grid-cols-3 gap-6"
        >
          {[
            { title: 'What we look for', body: 'A sharp point of view backed by lived experience or original research — not a resume.' },
            { title: 'Format', body: 'A 45–60 minute long-form conversation. Recorded remotely. Full episodes reach a global executive audience.' },
            { title: 'Response time', body: "I reply personally within two weeks if there's a fit. If you don't hear back, it isn't the moment — but it might be later." },
          ].map((c) => (
            <div key={c.title} className="p-6 rounded-2xl glass border border-white/8">
              <h3 className="text-sm font-black uppercase tracking-[0.25em] text-[#EF5B5B] mb-3">{c.title}</h3>
              <p className="text-sm text-gray-400 leading-relaxed">{c.body}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default ApplyPage;
